/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { isBefore, startOfMinute, subMinutes } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { isBoundaryError } from "../analytics/isBoundaryError";
import { useClockInTimeValidation } from "../hooks/useClockInTimeValidation";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../TimekeepingAdjustmentBottomSheet";
import { getDayIndicator } from "./utils/getDayIndicator";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockInTimeFormValues {
  clockInTime?: Date;
}

interface EditClockInTimeBottomSheetProps {
  modalState: UseModalState;
  clockInTime?: Date;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  isNfcShift: boolean;
  earliestGeofenceEntryTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  clockInButtonTappedAt?: Date;
  earlyClockInEnabled: boolean;
  earlyClockInEnabledInMinutes?: number;
  clockOutTime?: Date;
  breakStartTime?: Date;
  workerClockIn?: Date;
  isGeofenceFacility?: boolean;
  allowTimeAdjustmentRequest: boolean;
  shouldShowApprovalStep?: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockIn?: Date;
  shiftId?: string;
  workerId?: string;
  workplaceId?: string;
  timesheetFlow?: string;
  existingReason?: string;
  onConfirm: (clockInTime: Date, reason: string) => void;
  onCancel?: () => void;
}

const GEOFENCE_ENTRY_TIME_BUFFER_IN_MINUTES = 5;

export function EditClockInTimeBottomSheet(props: EditClockInTimeBottomSheetProps) {
  const {
    modalState,
    clockInTime,
    workplaceTimezone,
    shiftStartAt,
    shiftEndAt,
    isNfcShift,
    earliestGeofenceEntryTime,
    earliestPlausibleArrivalTime,
    clockInButtonTappedAt,
    earlyClockInEnabled,
    earlyClockInEnabledInMinutes,
    clockOutTime,
    breakStartTime,
    workerClockIn,
    isGeofenceFacility = false,
    allowTimeAdjustmentRequest,
    shouldShowApprovalStep = false,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockIn,
    shiftId,
    workerId,
    workplaceId,
    timesheetFlow,
    existingReason,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  const { setValue, watch, reset, formState } = useForm<EditClockInTimeFormValues>({
    defaultValues: {
      clockInTime: isDefined(clockInTime)
        ? getLocalTimeForPicker(clockInTime, workplaceTimezone)
        : undefined,
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockInTime)
        ? getLocalTimeForPicker(clockInTime, workplaceTimezone)
        : undefined;
      reset({ clockInTime: resetValue });
      setSaveAttempted(false);
    }
  }, [modalState.modalIsOpen, clockInTime, reset, workplaceTimezone]);

  const enforceNfcTimesheetBounds =
    useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
      defaultValue: false,
    }) && isNfcShift;

  const currentLocalClockInTime = watch("clockInTime");

  // Convert back to UTC for validation and submission
  const currentClockInTime = isDefined(currentLocalClockInTime)
    ? getUtcTimeFromPicker(currentLocalClockInTime, workplaceTimezone)
    : undefined;

  // --- New validation via hook (when MPR flag is on) ---
  const clockInValidation = useClockInTimeValidation({
    selectedTime: currentClockInTime,
    shiftId: shiftId ?? "",
    shiftStartAt,
    workplaceTimezone,
    geofenceArrivalTime: earliestGeofenceEntryTime,
    earliestPlausibleArrivalTime,
    workplaceSettings: {
      preventEarlyClockIns: !earlyClockInEnabled,
      earlyClockInMinutes: earlyClockInEnabledInMinutes,
    },
    isEnforceGeofenceTimestampsEnabled: isEnforceGeofenceTimestampsEnabled ?? false,
    verifiedClockIn,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceWorkplace: isGeofenceFacility,
    isNfcOrMeshWorkplace: isNfcShift,
    suppressMissedPunchWarning: false,
  });

  // --- Legacy validation (when MPR flag is off) ---
  const trueShiftStartTime = earlyClockInEnabled
    ? subMinutes(shiftStartAt, earlyClockInEnabledInMinutes ?? 0)
    : shiftStartAt;

  // Calculate "Earliest allowable clock in time" for regular shifts, "Latest" for NFC shifts
  const earliestAllowableClockInTime = useMemo(() => {
    const earliestGeofenceEntryTimeWithBuffer = isDefined(earliestGeofenceEntryTime)
      ? subMinutes(earliestGeofenceEntryTime, GEOFENCE_ENTRY_TIME_BUFFER_IN_MINUTES)
      : undefined;
    const times = [clockInButtonTappedAt, earliestGeofenceEntryTimeWithBuffer]
      .filter(isDefined)
      .map((t) => t.getTime());
    if (times.length === 0) {
      return undefined;
    }

    return startOfMinute(
      new Date(enforceNfcTimesheetBounds ? Math.max(...times) : Math.min(...times))
    );
  }, [clockInButtonTappedAt, earliestGeofenceEntryTime, enforceNfcTimesheetBounds]);

  const getLegacyValidationError = (timeToValidate: Date): string | undefined => {
    if (
      isDefined(earliestAllowableClockInTime) &&
      isBefore(trueShiftStartTime, earliestAllowableClockInTime) &&
      isBefore(timeToValidate, earliestAllowableClockInTime)
    ) {
      return `You cannot clock in prior to your recorded arrival time at ${formatTimeWithTimezone(
        earliestAllowableClockInTime,
        workplaceTimezone,
        false
      )}.`;
    }

    if (
      isDefined(earliestAllowableClockInTime) &&
      isBefore(earliestAllowableClockInTime, trueShiftStartTime) &&
      isBefore(timeToValidate, trueShiftStartTime)
    ) {
      return `Earliest permissible clock in time is ${formatTimeWithTimezone(
        trueShiftStartTime,
        workplaceTimezone,
        false
      )}`;
    }

    if (isDefined(clockOutTime) && !isBefore(timeToValidate, clockOutTime)) {
      return "You cannot clock in after your clock out time.";
    }

    if (isDefined(breakStartTime) && !isBefore(timeToValidate, breakStartTime)) {
      return "You cannot clock in after your break start time.";
    }

    return undefined;
  };

  const getValidationState = () => {
    if (allowTimeAdjustmentRequest) {
      return {
        errorMessage: clockInValidation.errorMessage,
        errorType: clockInValidation.errorType,
        geofenceValidationType: clockInValidation.geofenceValidationType,
        validationIntent: clockInValidation.intent,
        validationIntentContent: clockInValidation.intentContent,
        validationIntentIcon: clockInValidation.intentIcon,
      };
    }

    const legacyError = isDefined(currentClockInTime)
      ? getLegacyValidationError(currentClockInTime)
      : undefined;
    const legacyIntent =
      saveAttempted && isDefined(legacyError) ? getBottomSheetIntent("error") : undefined;

    return {
      errorMessage: legacyError,
      errorType: undefined,
      geofenceValidationType: undefined,
      validationIntent: legacyIntent?.intent,
      validationIntentContent: saveAttempted && isDefined(legacyError) ? legacyError : undefined,
      validationIntentIcon: legacyIntent?.intentIcon,
    };
  };

  const {
    errorMessage,
    errorType,
    geofenceValidationType,
    validationIntent,
    validationIntentContent,
    validationIntentIcon,
  } = getValidationState();

  const handlePickerConfirm = async (): Promise<boolean> => {
    setSaveAttempted(true);

    if (isBoundaryError(errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "clock-in",
        errorType,
        shiftId,
        workplaceId,
        selectedTime: currentClockInTime?.toISOString(),
        geofenceArrivalTime: earliestGeofenceEntryTime?.toISOString(),
        shiftStartAt: shiftStartAt.toISOString(),
        verifiedClockIn: verifiedClockIn?.toISOString(),
        earliestPlausibleArrivalTime: earliestPlausibleArrivalTime?.toISOString(),
        geofenceValidationType,
      });
    }

    if (errorMessage) {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_FORM_VALIDATION_ERROR, {
        shiftId,
        workerId,
        workplaceId,
        error: errorMessage,
        errorType,
        timekeepingAction: "clock-in",
        timesheetFlow,
      });
      return false;
    }

    if (allowTimeAdjustmentRequest) {
      return clockInValidation.validateOverlap();
    }

    return true;
  };

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
        earlyBufferMinutes: earlyClockInEnabledInMinutes ?? 30,
      });

      const timeToSet = inferResult.success ? inferResult.date : newTime;
      setValue("clockInTime", timeToSet, { shouldDirty: true });
      setSaveAttempted(false);
    }
  };

  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentClockInTime,
    referenceTimestamp: shiftStartAt,
    workplaceTimezone,
  });

  const requiresApproval =
    shouldShowApprovalStep &&
    isDefined(currentClockInTime) &&
    isDefined(workerClockIn) &&
    isBefore(currentClockInTime, workerClockIn);
  const showApprovalIntent = requiresApproval && !isPickerScrolling && !validationIntent;

  const isPickerValid = allowTimeAdjustmentRequest
    ? clockInValidation.isValid && !clockInValidation.isValidatingOverlap
    : isDefined(currentClockInTime);

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      intent={validationIntent ?? (showApprovalIntent ? "neutral" : undefined)}
      intentContent={
        validationIntentContent ??
        (showApprovalIntent
          ? "You are requesting an earlier clock in time. No worries - you'll be able to add the reason on the next screen."
          : undefined)
      }
      intentIcon={validationIntentIcon ?? (showApprovalIntent ? "info" : undefined)}
      pickerTitle="Adjust clock in"
      hasChanges={formState.isDirty}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={isPickerValid}
      requiresApproval={requiresApproval}
      reasonTitle="Why did you adjust your clock in time?"
      existingReason={existingReason}
      reasonHelperText={
        existingReason
          ? "Edit your message to the workplace to explain why you adjusted your clock in time."
          : undefined
      }
      onPickerConfirm={handlePickerConfirm}
      onConfirm={(reason) => {
        if (isDefined(currentClockInTime)) {
          onConfirm(currentClockInTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalClockInTime}
        dayIndicator={dayIndicator}
        containerSx={{
          alignSelf: "center",
        }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}
/* eslint-enable max-lines */
